import { useEffect, useMemo, useState, useRef } from 'react'
import {
  useGetBusinessGoalQuery,
  // useGetBusinessProfileQuery,
  // useGetMyProfileQuery,
  UserTask,
  UserTaskItemStatus,
  useGetUserTasksQuery,
  UserTaskQuickFilter,
  // BusinessUserType,
  BusinessGoalDateFilter,
  useGetAdvisorAssignedTasksQuery,
  useGetUserTaskQuery,
  useSearchUserTasksQuery,
  SearchUserTasksType,
} from '__generated__/api-types-and-hooks'
import { getTenantId } from 'utils/getTenantId'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
// import Avatar from 'appAssets/appImages/Avatar.png'
import { otherGoalIds, myOtherTasksGoal } from 'appConfig/data'
import { useFeatureFlag } from 'hooks/userFeatureFlag'
import { useGetBusinessProfile } from 'appHooks/useGetBusinessProfile'
import { getReturnToPath, isAdvisorRole } from 'utils/helper'
import { isString, debounce } from 'lodash'
import { GOALIDS } from 'appConfig/enums'
import { getEarliestStartDate, getLatestEndDate } from 'appHooks/useGetUserBusinessGoal'

interface IUseKanbanBoardDataProps {
  filter: Record<string, (string | number)[] | (string | number)>
  paramUserId?: string
  isArchiveView?: boolean
}

export const useKanbanBoardData = ({
  filter,
  paramUserId,
  isArchiveView,
}: IUseKanbanBoardDataProps) => {
  const tenantId = getTenantId()
  const { goalId, clientId } = useParams()
  const isAllTasks = otherGoalIds.includes(goalId)
  const [tasks, setTasks] = useState<UserTask[]>([])
  const tasksCount = tasks.length
  const [allGoaltasks, setAllGoalTasks] = useState<UserTask[]>([])
  const [isAllTasksCompleted, setIsAllTasksCompleted] = useState(false)
  const [showTaskDetails, setShowTaskDetails] = useState(false)
  const [goalName, setGoalName] = useState('')
  const [goalStartDate, setGoalStartDate] = useState('')
  const [goalEndDate, setGoalEndDate] = useState('')

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const userId = useSelector((state: RootState) => state.user.user.id)
  const isFieldViewForMiscPlaysDisabled = useFeatureFlag(
    paramUserId ?? clientId ?? userId,
    'release-field-view-misc-plays'
  )

  // Create debounced search function
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setDebouncedSearchTerm(value)
    }, 1000)
  ).current

  // Track when we have optimistic updates to prevent useEffect from overriding them
  const optimisticUpdateTime = useRef<number>(0)

  // Clean up debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  // Search query - determine search type based on current view
  const searchType = useMemo(() => {
    if (isAllTasks) {
      return goalId === GOALIDS.MY_ALL_PLAYS
        ? SearchUserTasksType.AllTasks
        : SearchUserTasksType.MiscTasks
    } else {
      return SearchUserTasksType.GoalTasks
    }
  }, [goalId, isAllTasks])

  // Search tasks query — only send userId (clientId) in client view; omit for advisor My Goal Center
  const { data: searchResults, isLoading: isSearching } = useSearchUserTasksQuery(
    {
      input: {
        tenantId,
        userId: paramUserId ?? clientId ?? undefined,
        goalId: isAllTasks ? undefined : goalId,
        searchTerm: debouncedSearchTerm,
        searchType,
      },
    },
    {
      enabled: !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch business profile
  const {
    advisors,
    assigneeOptions,
    userName,
    userFirstName,
    userLastName,
    advisorsData,
    userAvatar,
    logo,
    hoursSpentPerDay,
  } = useGetBusinessProfile({ paramUserId, enabled: !!paramUserId })

  // Fetch business goal
  const {
    data: businessGoalData,
    isLoading: isGoalLoading,
    refetch,
  } = useGetBusinessGoalQuery(
    {
      input: { goalId, userId: paramUserId ?? clientId ?? userId, tenantId, filter },
    },
    {
      refetchOnWindowFocus: true,
      enabled: !!goalId && !!filter,
      cacheTime: 0,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch business goal without filter. This is used for progress bar
  const { data: businessGoal, refetch: refetchBusinessGoal } = useGetBusinessGoalQuery(
    {
      input: {
        goalId,
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: { dateRange: BusinessGoalDateFilter.EntireGamePlan },
      },
    },
    {
      refetchOnWindowFocus: true,
      cacheTime: 0,
      enabled: !!goalId && !isAllTasks,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch All tasks if goalID matches. This is used for progress bar
  const { data: otherBusinessGoalTasks, refetch: refetchOtherBusinessGoalTasks } =
    useGetUserTasksQuery(
      {
        input: {
          userId: paramUserId ?? clientId ?? userId,
          tenantId,
          filter: {
            quickFilter:
              goalId === 'all-plays'
                ? UserTaskQuickFilter.AllGoalsTasks
                : UserTaskQuickFilter.NoProjectAssigned,
          },
        },
      },
      {
        enabled: isAllTasks,
        refetchOnWindowFocus: true,
        cacheTime: 0,
        refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
      }
    )

  //Fetch All tasks if goalID matches. This is used for getting all goal names for filtering by goalName
  const { data: allTasks } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
      },
    },
    {
      // enabled: isAllTasks,
      enabled: goalId === GOALIDS.MY_ALL_PLAYS && (clientId || !isAdvisorRole()) ? true : false,
      refetchOnWindowFocus: true,
      cacheTime: 0,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  //Fetch All tasks if goalID matches
  const {
    data: otherGoalTasks,
    isLoading: isOtherTasksLoading,
    refetch: refetchTasks,
  } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: {
          ...filter,
          quickFilter:
            goalId === 'all-plays'
              ? isAdvisorRole() && !clientId
                ? UserTaskQuickFilter.NoProjectAssigned
                : UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
        },
      },
    },
    {
      enabled: isAllTasks && !isArchiveView, // Disable this query when in archive view
      refetchOnWindowFocus: true,
      cacheTime: 1000 * 60 * 5, // Consistent caching
      staleTime: 1000 * 10, // Reduce stale time to 10 seconds for faster updates
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  const { data: advisorTasks, isLoading: isAdvisorTasksLoading } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        userId,
        tenantId,
        filter: {
          ...filter,
          quickFilter:
            goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
        },
      },
    },
    {
      enabled: isAllTasks && !clientId && isAdvisorRole() && !isArchiveView,
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // get all advisor tasks for filtering by businessName
  const { data: allAdvisorTasks } = useGetAdvisorAssignedTasksQuery(
    {
      input: { userId, tenantId },
    },
    {
      enabled: isAllTasks && !clientId && isAdvisorRole(),
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch archived tasks when in archive view
  const {
    data: archivedTasks,
    isLoading: isArchivedTasksLoading,
    refetch: refetchArchivedTasks,
  } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: {
          ...filter,
          quickFilter:
            goalId === 'all-plays'
              ? isAdvisorRole() && !clientId
                ? UserTaskQuickFilter.NoProjectAssigned
                : UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Archived,
        },
      },
    },
    {
      enabled: isAllTasks && isArchiveView,
      refetchOnWindowFocus: true,
      cacheTime: 1000 * 60 * 5,
      staleTime: 1000 * 10,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch deleted tasks when in archive view
  const {
    data: deletedTasks,
    isLoading: isDeletedTasksLoading,
    refetch: refetchDeletedTasks,
  } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: {
          ...filter,
          quickFilter:
            goalId === 'all-plays'
              ? isAdvisorRole() && !clientId
                ? UserTaskQuickFilter.NoProjectAssigned
                : UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Deleted,
        },
      },
    },
    {
      enabled: isAllTasks && isArchiveView,
      refetchOnWindowFocus: true,
      cacheTime: 1000 * 60 * 5,
      staleTime: 1000 * 10,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch archived advisor tasks when in archive view
  const {
    data: archivedAdvisorTasks,
    isLoading: isArchivedAdvisorTasksLoading,
    refetch: refetchArchivedAdvisorTasks,
  } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        userId,
        tenantId,
        filter: {
          ...filter,
          quickFilter:
            goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Archived,
        },
      },
    },
    {
      enabled: isAllTasks && !clientId && isAdvisorRole() && isArchiveView,
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch deleted advisor tasks when in archive view
  const {
    data: deletedAdvisorTasks,
    isLoading: isDeletedAdvisorTasksLoading,
    refetch: refetchDeletedAdvisorTasks,
  } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        userId,
        tenantId,
        filter: {
          ...filter,
          quickFilter:
            goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Deleted,
        },
      },
    },
    {
      enabled: isAllTasks && !clientId && isAdvisorRole() && isArchiveView,
      refetchOnWindowFocus: true,
      refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
    }
  )

  // Fetch ALL tasks without date/filter restrictions for global search
  const { data: allTasksForGlobalSearch } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: {
          quickFilter:
            goalId === 'all-plays'
              ? isAdvisorRole() && !clientId
                ? UserTaskQuickFilter.NoProjectAssigned
                : UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          dateRange: BusinessGoalDateFilter.EntireGamePlan,
        },
      },
    },
    {
      enabled: isAllTasks && !isArchiveView && !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch ALL advisor tasks without date/filter restrictions for global search
  const { data: allAdvisorTasksForGlobalSearch } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        userId,
        tenantId,
        filter: {
          quickFilter:
            goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          dateRange: BusinessGoalDateFilter.EntireGamePlan,
        },
      },
    },
    {
      enabled:
        isAllTasks &&
        !clientId &&
        isAdvisorRole() &&
        !isArchiveView &&
        !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch ALL archived tasks without date/filter restrictions for global search
  const { data: allArchivedTasksForGlobalSearch } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: {
          quickFilter:
            goalId === 'all-plays'
              ? isAdvisorRole() && !clientId
                ? UserTaskQuickFilter.NoProjectAssigned
                : UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Archived,
          dateRange: BusinessGoalDateFilter.EntireGamePlan,
        },
      },
    },
    {
      enabled: isAllTasks && isArchiveView && !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch ALL deleted tasks without date/filter restrictions for global search
  const { data: allDeletedTasksForGlobalSearch } = useGetUserTasksQuery(
    {
      input: {
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: {
          quickFilter:
            goalId === 'all-plays'
              ? isAdvisorRole() && !clientId
                ? UserTaskQuickFilter.NoProjectAssigned
                : UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Deleted,
          dateRange: BusinessGoalDateFilter.EntireGamePlan,
        },
      },
    },
    {
      enabled: isAllTasks && isArchiveView && !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch ALL archived advisor tasks without date/filter restrictions for global search
  const { data: allArchivedAdvisorTasksForGlobalSearch } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        userId,
        tenantId,
        filter: {
          quickFilter:
            goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Archived,
          dateRange: BusinessGoalDateFilter.EntireGamePlan,
        },
      },
    },
    {
      enabled:
        isAllTasks && !clientId && isAdvisorRole() && isArchiveView && !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch ALL deleted advisor tasks without date/filter restrictions for global search
  const { data: allDeletedAdvisorTasksForGlobalSearch } = useGetAdvisorAssignedTasksQuery(
    {
      input: {
        userId,
        tenantId,
        filter: {
          quickFilter:
            goalId === 'all-plays'
              ? UserTaskQuickFilter.AllGoalsTasks
              : UserTaskQuickFilter.NoProjectAssigned,
          status: UserTaskItemStatus.Deleted,
          dateRange: BusinessGoalDateFilter.EntireGamePlan,
        },
      },
    },
    {
      enabled:
        isAllTasks && !clientId && isAdvisorRole() && isArchiveView && !!debouncedSearchTerm.trim(),
      refetchOnWindowFocus: false,
    }
  )

  // Fetch goal tasks without date/filter restrictions for global search
  const { data: businessGoalDataForGlobalSearch } = useGetBusinessGoalQuery(
    {
      input: {
        goalId,
        userId: paramUserId ?? clientId ?? userId,
        tenantId,
        filter: { dateRange: BusinessGoalDateFilter.EntireGamePlan },
      },
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!goalId && !isAllTasks && !!debouncedSearchTerm.trim(),
    }
  )

  const advisorAssignedBusinessNames = useMemo(() => {
    if (
      allAdvisorTasks?.getAdvisorAssignedTasks &&
      allAdvisorTasks.getAdvisorAssignedTasks.length > 0
    ) {
      const uniqueBusinessNames = new Set<string>()
      const result: { label: string; value: string }[] = []

      allAdvisorTasks.getAdvisorAssignedTasks.forEach((task) => {
        const businessName = task.businessName ?? ''
        if (businessName && !uniqueBusinessNames.has(businessName)) {
          uniqueBusinessNames.add(businessName)
          result.push({ label: businessName, value: businessName })
        }
      })
      return result.sort((a, b) => a.label.localeCompare(b.label))
    } else return []
  }, [allAdvisorTasks])

  const allGoalNames = useMemo(() => {
    if (allTasks?.getUserTasks && allTasks.getUserTasks.length > 0) {
      const uniqueGoalNames = new Set<string>()
      const result: { label: string; value: string }[] = []

      allTasks.getUserTasks.forEach((task) => {
        const goalName = task.goalName ?? ''
        if (goalName && !uniqueGoalNames.has(goalName)) {
          uniqueGoalNames.add(goalName)
          result.push({ label: goalName, value: task.goalId ?? '' })
        }
      })
      const sortedGoalNames = result.sort((a, b) => a.label.localeCompare(b.label))
      return sortedGoalNames
    }

    if (
      allAdvisorTasks?.getAdvisorAssignedTasks &&
      allAdvisorTasks.getAdvisorAssignedTasks.length > 0
    ) {
      const uniqueGoalNames = new Set<string>()
      const result: { label: string; value: string }[] = []

      allAdvisorTasks.getAdvisorAssignedTasks.forEach((task) => {
        const goalName = task.goalName ?? ''
        if (goalName && !uniqueGoalNames.has(goalName)) {
          uniqueGoalNames.add(goalName)
          result.push({ label: goalName, value: task.goalId ?? '' })
        }
      })
      const sortedGoalNames = result.sort((a, b) => a.label.localeCompare(b.label))
      return sortedGoalNames
    }

    return []
  }, [allTasks, allAdvisorTasks?.getAdvisorAssignedTasks])

  const isViewFieldButton: boolean = useMemo(
    () => !(isAllTasks && isFieldViewForMiscPlaysDisabled),
    [isFieldViewForMiscPlaysDisabled, isAllTasks]
  )

  // Get plan from businessGoalData
  const plan = useMemo(() => businessGoalData?.getBusinessGoal?.plan, [businessGoalData])

  // Get task plan from plan
  const taskPlan = useMemo(
    () =>
      plan
        ? {
            name: plan.name,
            id: plan.planId,
            planExpectedEndDate: plan.expectedEndDate,
            startDate: plan.startDate,
          }
        : undefined,
    [plan]
  )

  // Get milestones from the plan
  const taskMilestones = useMemo(
    () =>
      plan
        ? plan.milestones?.map((milestone) => ({ name: milestone.name, id: milestone.milestoneId }))
        : undefined,
    [plan]
  )

  // Function to get tasks by status
  const getTasksByStatus = (status: UserTaskItemStatus) =>
    tasks.filter((task: UserTask) => task.status === status)

  // Memoize arrays to prevent unnecessary re-renders
  const otherTasksArray = useMemo(
    () => otherGoalTasks?.getUserTasks || [],
    [otherGoalTasks?.getUserTasks]
  )
  const advisorTasksArray = useMemo(
    () => advisorTasks?.getAdvisorAssignedTasks || [],
    [advisorTasks?.getAdvisorAssignedTasks]
  )
  const searchTasksArray = useMemo(
    () => searchResults?.searchUserTasks || [],
    [searchResults?.searchUserTasks]
  )
  const planTasks = useMemo(() => plan?.tasks || [], [plan?.tasks])

  // Memoize archive arrays
  const archivedTasksArray = useMemo(
    () => archivedTasks?.getUserTasks || [],
    [archivedTasks?.getUserTasks]
  )
  const deletedTasksArray = useMemo(
    () => deletedTasks?.getUserTasks || [],
    [deletedTasks?.getUserTasks]
  )
  const archivedAdvisorTasksArray = useMemo(
    () => archivedAdvisorTasks?.getAdvisorAssignedTasks || [],
    [archivedAdvisorTasks?.getAdvisorAssignedTasks]
  )
  const deletedAdvisorTasksArray = useMemo(
    () => deletedAdvisorTasks?.getAdvisorAssignedTasks || [],
    [deletedAdvisorTasks?.getAdvisorAssignedTasks]
  )

  // Memoize global search arrays
  const allTasksForGlobalSearchArray = useMemo(
    () => allTasksForGlobalSearch?.getUserTasks || [],
    [allTasksForGlobalSearch?.getUserTasks]
  )
  const allAdvisorTasksForGlobalSearchArray = useMemo(
    () => allAdvisorTasksForGlobalSearch?.getAdvisorAssignedTasks || [],
    [allAdvisorTasksForGlobalSearch?.getAdvisorAssignedTasks]
  )
  const allArchivedTasksForGlobalSearchArray = useMemo(
    () => allArchivedTasksForGlobalSearch?.getUserTasks || [],
    [allArchivedTasksForGlobalSearch?.getUserTasks]
  )
  const allDeletedTasksForGlobalSearchArray = useMemo(
    () => allDeletedTasksForGlobalSearch?.getUserTasks || [],
    [allDeletedTasksForGlobalSearch?.getUserTasks]
  )
  const allArchivedAdvisorTasksForGlobalSearchArray = useMemo(
    () => allArchivedAdvisorTasksForGlobalSearch?.getAdvisorAssignedTasks || [],
    [allArchivedAdvisorTasksForGlobalSearch?.getAdvisorAssignedTasks]
  )
  const allDeletedAdvisorTasksForGlobalSearchArray = useMemo(
    () => allDeletedAdvisorTasksForGlobalSearch?.getAdvisorAssignedTasks || [],
    [allDeletedAdvisorTasksForGlobalSearch?.getAdvisorAssignedTasks]
  )
  const planTasksForGlobalSearch = useMemo(
    () => businessGoalDataForGlobalSearch?.getBusinessGoal?.plan?.tasks || [],
    [businessGoalDataForGlobalSearch?.getBusinessGoal?.plan?.tasks]
  )

  const hasGoalStarted = useMemo(
    () =>
      businessGoalData?.getBusinessGoal?.status !== UserTaskItemStatus.Todo ||
      businessGoalData?.getBusinessGoal?.plan?.tasks?.some(
        (task) => task?.status !== UserTaskItemStatus.Todo
      ),
    [businessGoalData]
  )

  // useEffect to set tasks when plan is updated
  useEffect(() => {
    console.log('🔄 Hook useEffect triggered with dependencies:', {
      filter,
      isAllTasks,
      otherTasksArrayLength: otherTasksArray.length,
      advisorTasksArrayLength: advisorTasksArray.length,
      debouncedSearchTerm,
      searchTasksArrayLength: searchTasksArray.length,
      planTasksLength: planTasks.length,
    })

    // If search is active, search globally across ALL tasks without filters
    // Prefer backend results when available, otherwise fall back to client-side filtering of ALL task sets
    if (debouncedSearchTerm.trim()) {
      const normalizedQuery = debouncedSearchTerm.trim().toLowerCase()
      const tokens = normalizedQuery.split(/\s+/).filter(Boolean)

      let sourceTasks: UserTask[] = []

      if (searchTasksArray.length > 0) {
        // backend returned results; filter by title and description
        sourceTasks = searchTasksArray as UserTask[]
      } else {
        // backend returned no results; filter over ALL tasks globally (ignoring current filters)
        if (isArchiveView) {
          // For archive view, use ALL archived and deleted tasks without filter restrictions
          sourceTasks = [
            ...allArchivedTasksForGlobalSearchArray,
            ...allDeletedTasksForGlobalSearchArray,
            ...allArchivedAdvisorTasksForGlobalSearchArray,
            ...allDeletedAdvisorTasksForGlobalSearchArray,
          ]
        } else if (!isAllTasks) {
          // For specific goal view, use ALL tasks for that goal without date restrictions
          sourceTasks = planTasksForGlobalSearch as UserTask[]
        } else {
          // For all tasks view, use ALL tasks without filter restrictions
          sourceTasks = [...allTasksForGlobalSearchArray, ...allAdvisorTasksForGlobalSearchArray]
        }
      }

      const strictlyMatched = sourceTasks.filter((task) => {
        const title = (task?.name ?? '').toLowerCase()
        const description = (task?.instructionForTask ?? '').toLowerCase()
        return tokens.every((tk) => title.includes(tk) || description.includes(tk))
      })

      setTasks(filterTasksAfterReOrder(strictlyMatched))
      return
    }

    // Check if we have a recent optimistic update (within last 3 seconds)
    const now = Date.now()
    const timeSinceOptimisticUpdate = now - optimisticUpdateTime.current

    // Only skip if we have tasks already loaded AND there's a recent optimistic update
    if (tasksCount > 0 && timeSinceOptimisticUpdate < 3000) {
      console.log(
        '⏸️ Skipping useEffect due to recent optimistic update:',
        timeSinceOptimisticUpdate + 'ms ago'
      )
      return
    }

    // If no tasks are loaded yet, always load them (initial load)
    if (tasksCount === 0) {
      console.log('🚀 Initial load - loading tasks regardless of optimistic update time')
    }

    let tasksToSet: UserTask[] = []

    // If there's a specific plan being viewed and it has tasks, prioritize those
    if (filter && planTasks.length > 0) {
      console.log('📋 Using plan tasks')
      tasksToSet = filterTasksAfterReOrder(planTasks)
    } else if (!isAllTasks) {
      // If not "All Tasks" view, and no specific plan tasks, set to empty
      console.log('📭 Setting empty tasks (not all tasks view)')
      tasksToSet = []
    } else {
      // Check if we're in archive view
      if (isArchiveView) {
        // In archive view, use archived and deleted tasks
        const hasBusinessNameFilter =
          filter?.businessName &&
          Array.isArray(filter.businessName) &&
          filter.businessName.length > 0

        if (hasBusinessNameFilter) {
          // If 'businessName' filter is active, only include archived advisor tasks
          tasksToSet = filterTasksAfterReOrder([
            ...archivedAdvisorTasksArray,
            ...deletedAdvisorTasksArray,
          ])
        } else {
          // Combine all archived and deleted tasks
          tasksToSet = filterTasksAfterReOrder([
            ...archivedTasksArray,
            ...deletedTasksArray,
            ...archivedAdvisorTasksArray,
            ...deletedAdvisorTasksArray,
          ])
        }
      } else {
        // Normal view - use active tasks
        const hasBusinessNameFilter =
          filter?.businessName &&
          Array.isArray(filter.businessName) &&
          filter.businessName.length > 0

        if (hasBusinessNameFilter) {
          // If 'businessName' filter is active, only include allAdvisorTasks
          tasksToSet = filterTasksAfterReOrder(advisorTasksArray)
        } else {
          // If 'businessName' filter is NOT active, combine allOtherTasks and allAdvisorTasks
          tasksToSet = filterTasksAfterReOrder([...otherTasksArray, ...advisorTasksArray])
        }
      }
    }

    console.log(
      '📊 Hook setting tasks:',
      tasksToSet.map((t) => ({ id: t.taskId, name: t.name, status: t.status }))
    )
    setTasks(tasksToSet)
  }, [
    filter,
    isAllTasks,
    otherTasksArray,
    advisorTasksArray,
    debouncedSearchTerm,
    searchTasksArray,
    planTasks,
    tasksCount,
    isArchiveView,
    archivedTasksArray,
    deletedTasksArray,
    archivedAdvisorTasksArray,
    deletedAdvisorTasksArray,
    allTasksForGlobalSearchArray,
    allAdvisorTasksForGlobalSearchArray,
    allArchivedTasksForGlobalSearchArray,
    allDeletedTasksForGlobalSearchArray,
    allArchivedAdvisorTasksForGlobalSearchArray,
    allDeletedAdvisorTasksForGlobalSearchArray,
    planTasksForGlobalSearch,
  ])

  // Memoize arrays for progress bar calculation
  const otherBusinessGoalTasksArray = useMemo(
    () => otherBusinessGoalTasks?.getUserTasks || [],
    [otherBusinessGoalTasks?.getUserTasks]
  )
  const businessGoalPlanTasks = useMemo(
    () => businessGoal?.getBusinessGoal?.plan?.tasks || [],
    [businessGoal?.getBusinessGoal?.plan?.tasks]
  )

  // set all tasks for progress bar
  useEffect(() => {
    const allOtherTasks = [...otherBusinessGoalTasksArray, ...advisorTasksArray]
    const allTasks = isAllTasks ? allOtherTasks : businessGoalPlanTasks
    setAllGoalTasks((allTasks ?? []) as UserTask[])
    if (allTasks?.length > 0) {
      setIsAllTasksCompleted(
        allTasks
          ?.filter(
            (at) =>
              at?.status !== UserTaskItemStatus.Archived &&
              at?.status !== UserTaskItemStatus.Deleted
          )
          .every((task) => task?.status === UserTaskItemStatus.Done) ?? false
      )
    }
  }, [isAllTasks, otherBusinessGoalTasksArray, advisorTasksArray, businessGoalPlanTasks])

  const queryParam = getReturnToPath()
  const selectedTaskId =
    queryParam.selectedTaskId && isString(queryParam.selectedTaskId)
      ? queryParam.selectedTaskId ?? ''
      : ''
  const { data: selectedTaskResponse } = useGetUserTaskQuery(
    {
      input: {
        taskId: selectedTaskId,
        tenantId,
        userId: paramUserId ?? clientId ?? userId,
      },
    },
    { enabled: !!selectedTaskId }
  )
  useEffect(() => {
    if (selectedTaskId) {
      setShowTaskDetails(true)
    }
  }, [selectedTaskId])

  const handleRefetch = () => {
    if (isAllTasks) {
      if (isArchiveView) {
        // Refetch archived and deleted tasks when in archive view
        refetchArchivedTasks()
        refetchDeletedTasks()
        if (!clientId && isAdvisorRole()) {
          refetchArchivedAdvisorTasks()
          refetchDeletedAdvisorTasks()
        }
      } else {
        // Refetch active tasks when not in archive view
        refetchTasks()
        refetchOtherBusinessGoalTasks()
      }
    } else if (goalId) {
      // refetch() bypasses React Query's `enabled`, so skip it when there is no
      // goalId (e.g. the dashboard) — getBusinessGoal requires goalId: ID!.
      refetch()
      refetchBusinessGoal()
    }
  }

  // Handler for search input changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    debouncedSearch(value)
  }

  // Set goal name only when goalId changes and data is available
  useEffect(() => {
    if (goalId) {
      if (isAllTasks) {
        setGoalName(myOtherTasksGoal.find((goal) => goal.goalId === goalId)?.name || '')
      } else if (businessGoalData?.getBusinessGoal?.name) {
        setGoalName(businessGoalData.getBusinessGoal.name)
      }
    }
    // Only run when goalId changes or first businessGoalData load
    // eslint-disable-next-line
  }, [goalId, isAllTasks, businessGoalData?.getBusinessGoal?.name])

  // Set goal start and end date only when goalId changes and values actually change
  useEffect(() => {
    if (!goalId) return

    let nextStart = ''
    let nextEnd = ''

    if (!isAllTasks) {
      if (businessGoalData?.getBusinessGoal?.startDate) {
        nextStart = businessGoalData?.getBusinessGoal?.startDate
        setGoalStartDate(nextStart)
      }

      if (businessGoalData?.getBusinessGoal?.expectedEndDate) {
        nextEnd = businessGoalData?.getBusinessGoal?.expectedEndDate
        setGoalEndDate(nextEnd)
      }
    } else {
      const tasks = [...(otherBusinessGoalTasksArray || []), ...(advisorTasksArray || [])]
      nextStart = getEarliestStartDate(tasks)
      nextEnd = getLatestEndDate(tasks)
      setGoalStartDate(nextStart)
      setGoalEndDate(nextEnd)
    }

    // eslint-disable-next-line
  }, [
    goalId,
    isAllTasks,
    businessGoalData?.getBusinessGoal?.startDate,
    businessGoalData?.getBusinessGoal?.expectedEndDate,
    otherBusinessGoalTasksArray,
    advisorTasksArray,
  ])

  return {
    userName,
    userFirstName,
    userLastName,
    advisorsData,
    logo,
    userAvatar,
    plan,
    taskMilestones,
    isLoading:
      isGoalLoading ||
      (isSearching && !!debouncedSearchTerm.trim()) ||
      isOtherTasksLoading ||
      isAdvisorTasksLoading ||
      (isArchiveView &&
        (isArchivedTasksLoading ||
          isDeletedTasksLoading ||
          isArchivedAdvisorTasksLoading ||
          isDeletedAdvisorTasksLoading)),
    userId: clientId ?? userId,
    tenantId,
    tasks,
    allGoaltasks,
    isAllTasksCompleted,
    taskPlan,
    goalName,
    goalId,
    hoursSpentPerDay,
    setTasks,
    goalStartDate,
    goalEndDate,
    getTasksByStatus,
    refetch: handleRefetch,
    boAdvisors: advisors,
    isViewFieldButton,
    assigneeOptions,
    showTaskDetails,
    setShowTaskDetails,
    selectedTask: selectedTaskResponse?.getUserTask,
    advisorAssignedBusinessNames,
    isAllTasks,
    isAdvisorRole,
    clientId,
    // Search functionality
    searchQuery,
    handleSearchChange,
    hasGoalStarted: isAllTasks ? true : hasGoalStarted,
    isFetchingTasks:
      isGoalLoading ||
      isOtherTasksLoading ||
      isAdvisorTasksLoading ||
      (isArchiveView &&
        (isArchivedTasksLoading ||
          isDeletedTasksLoading ||
          isArchivedAdvisorTasksLoading ||
          isDeletedAdvisorTasksLoading)),
    allGoalNames,
    // Optimistic update tracking
    optimisticUpdateTime,
  }
}

// Sort tasks by 'order' in ascending order
const filterTasksAfterReOrder = (tasks) => {
  const allTasks = tasks.sort((a: UserTask, b: UserTask) => {
    if (a.order && b.order) return a.order - b.order
    return 0
  })
  return allTasks
}
